import {
  ArrowLeftIcon,
  BellIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CogIcon,
  CreditCardIcon,
  HandIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  PencilAltIcon,
  TruckIcon,
  LightningBoltIcon,
  UserCircleIcon,
  PuzzleIcon,
  SupportIcon,
  ReceiptTaxIcon,
  FireIcon,
  DocumentIcon,
  GiftIcon,
  RefreshIcon,
  DesktopComputerIcon,
} from '@heroicons/react/outline';
import { ReactComponent as DoorOpenIcon } from '../assets/icons/DoorOpenIcon.svg';
import { useStore } from 'context/store-context';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import classNames from 'classnames';
import ReturnsScopeWarning from 'components/ReturnsScopeWarning';
import { SidebarToggleIcon } from './SidebarToggleIcon';

interface SubNavItem {
  key: string;
  name: string;
  icon?: React.ReactNode;
  to?: string;
  children?: SubNavItem[];
  disabled?: boolean;
  badge?: string;
}

interface NavItem {
  key: string;
  name: string;
  icon: React.ReactNode;
  to?: string;
  children?: SubNavItem[];
  disabled?: boolean;
  badge?: string;
}

interface SettingsViewWrapperProps {
  children: React.ReactNode;
  onClose?: () => void;
  activeNav?: string;
  sectionId?: string;
  onNavChange?: (navId: string) => void;
}

const getSettingsExitLabel = (route: string | null) => {
  if (!route) return 'Exit settings';

  const path = route.split('?')[0];
  const routeLabels = [
    { prefix: '/dashboard', label: 'Home' },
    { prefix: '/orders', label: 'Orders' },
    { prefix: '/order-editing', label: 'Order Editing' },
    { prefix: '/claims', label: 'Issues' },
    { prefix: '/returns', label: 'Returns & Exchanges' },
    { prefix: '/referral-program', label: 'Refer & Earn' },
    { prefix: '/merchants', label: 'Merchants' },
    { prefix: '/admins', label: 'Admins' },
    { prefix: '/announcements', label: 'Announcements' },
    { prefix: '/install-requests', label: 'Install Requests' },
  ];

  const match = routeLabels.find((item) => path.startsWith(item.prefix));

  return match ? `Back to ${match.label}` : 'Exit settings';
};

const navigationItems: NavItem[] = [
  {
    name: 'Delivery Guarantee',
    icon: <ShieldCheckIcon className="w-5 h-5" />,
    to: '/settings/protection',
    key: 'protection',
  },
  {
    name: 'Impact',
    icon: <FireIcon className="w-5 h-5" />,
    to: '/settings/impact',
    key: 'impact',
  },
  {
    name: 'Resolution',
    icon: <SupportIcon className="w-5 h-5" />,
    key: 'resolution-portal',
    children: [
      {
        name: 'General',
        icon: <CogIcon className="w-5 h-5" />,
        to: '/settings/resolution-portal/general',
        key: 'resolution-portal/general',
      },
      {
        name: 'Portal',
        icon: <DoorOpenIcon className="w-5 h-5" />,
        to: '/settings/resolution-portal/portal',
        key: 'resolution-portal/portal',
      },
      {
        name: 'Resolutions',
        icon: <HandIcon className="w-5 h-5" />,
        to: '/settings/resolution-portal/resolutions',
        key: 'resolution-portal/resolutions',
      },
    ],
  },
  {
    name: 'Returns & Exchanges',
    icon: <RefreshIcon className="w-5 h-5" />,
    key: 'returns-settings',
    children: [
      {
        name: 'Policy',
        icon: <DocumentIcon className="w-5 h-5" />,
        to: '/settings/resolution-portal/returns/policy',
        key: 'resolution-portal/returns/policy',
      },
      {
        name: 'Returns',
        icon: <RefreshIcon className="w-5 h-5" />,
        to: '/settings/resolution-portal/returns/settings',
        key: 'resolution-portal/returns/settings',
      },
      {
        name: 'Exchanges',
        icon: <RefreshIcon className="w-5 h-5" />,
        to: '/settings/resolution-portal/returns/exchanges',
        key: 'resolution-portal/returns/exchanges',
      },
      {
        name: 'Incentives',
        icon: <GiftIcon className="w-5 h-5" />,
        to: '/settings/resolution-portal/returns/incentives',
        key: 'resolution-portal/returns/incentives',
      },
      {
        name: 'Shipping',
        icon: <TruckIcon className="w-5 h-5" />,
        to: '/settings/resolution-portal/returns/carriers',
        key: 'resolution-portal/returns/carriers',
      },
      {
        name: 'Review',
        icon: <ShieldCheckIcon className="w-5 h-5" />,
        to: '/settings/resolution-portal/returns/approval',
        key: 'resolution-portal/returns/approval',
      },
      {
        name: 'Notifications',
        icon: <BellIcon className="w-5 h-5" />,
        to: '/settings/notifications?tab=returns',
        key: 'resolution-portal/returns/notifications',
      },
    ],
  },
  {
    name: 'Order Editing',
    icon: <PencilAltIcon className="w-5 h-5" />,
    key: 'order-edit',
    children: [
      {
        name: 'Customization',
        to: '/settings/order-settings/customization',
        key: 'order-settings/customization',
      },
      {
        name: 'Editing rules',
        to: '/settings/order-settings/rules',
        key: 'order-settings/rules',
      },
    ],
  },
  {
    name: 'Notifications',
    icon: <BellIcon className="w-5 h-5" />,
    to: '/settings/notifications',
    key: 'notifications',
  },
  {
    name: 'Tracking',
    icon: <TruckIcon className="w-5 h-5" />,
    to: '/settings/tracking-templates',
    key: 'tracking-templates',
  },
  {
    name: 'Automations',
    icon: <LightningBoltIcon className="w-5 h-5" />,
    to: '/settings/claim-automations',
    key: 'claim-automations',
  },
  {
    name: 'Integrations',
    icon: <PuzzleIcon className="w-5 h-5" />,
    to: '/settings/integration',
    key: 'integration',
  },
  {
    name: 'Team',
    icon: <UserGroupIcon className="w-5 h-5" />,
    to: '/settings/team',
    key: 'team',
  },
  {
    name: 'Billing',
    icon: <CreditCardIcon className="w-5 h-5" />,
    key: 'billing',
    children: [
      {
        name: 'Invoices',
        icon: <ReceiptTaxIcon className="w-5 h-5" />,
        to: '/settings/billing/invoices',
        key: 'billing/invoices',
      },
      {
        name: 'Profile',
        icon: <CogIcon className="w-5 h-5" />,
        to: '/settings/billing/profile',
        key: 'billing/profile',
      },
    ],
  },
  {
    name: 'Account',
    icon: <UserCircleIcon className="w-5 h-5" />,
    to: '/settings/account',
    key: 'account',
  },
];

const portalActions = [
  {
    name: 'Resolution Portal',
    icon: DesktopComputerIcon,
    url: 'https://resolve.shipaid.com/',
    key: 'resolution-portal',
  },
];

const SettingsViewWrapper: React.FC<SettingsViewWrapperProps> = ({
  children,
  onClose,
  activeNav = 'general',
  sectionId,
  onNavChange,
}) => {
  const navigate = useNavigate();
  const { storeName, storeProperties } = useStore();
  const [currentNav, setCurrentNav] = useState(activeNav);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('settings-sidebar-collapsed');
    return saved === 'true';
  });
  useEffect(() => {
    if (activeNav !== 'claim-automations' && activeNav !== 'protection') return;
    const fitClaimAutomations = () => {
      if (window.innerWidth < 640) setIsSidebarCollapsed(true);
    };
    fitClaimAutomations();
    window.addEventListener('resize', fitClaimAutomations);
    return () => window.removeEventListener('resize', fitClaimAutomations);
  }, [activeNav]);
  const [logoError, setLogoError] = useState(false);
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const exitButtonLabel = getSettingsExitLabel(
    typeof window !== 'undefined'
      ? sessionStorage.getItem('settingsReturnRoute')
      : null,
  );

  useEffect(() => {
    if (sectionId === 'order-edit' && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [location.pathname, sectionId]);

  // Save return route when entering settings
  useEffect(() => {
    const currentPath = location.pathname;
    const isInSettings =
      currentPath.startsWith('/settings') ||
      currentPath.startsWith('/order-settings');

    // Get previous path from sessionStorage BEFORE updating it
    const previousPath = sessionStorage.getItem('lastRoute');
    const wasInSettings =
      previousPath?.startsWith('/settings') ||
      previousPath?.startsWith('/order-settings');

    // Save the route we came from (before entering settings) for navigation back
    // This is a fallback in case the route wasn't saved in Sidebar (e.g., direct navigation)
    if (
      isInSettings &&
      !wasInSettings &&
      previousPath &&
      !sessionStorage.getItem('settingsReturnRoute')
    ) {
      sessionStorage.setItem('settingsReturnRoute', previousPath);
    }

    if (!isInSettings) {
      // Clear the return route when leaving settings
      sessionStorage.removeItem('settingsReturnRoute');
    }

    // Update sessionStorage with current path
    sessionStorage.setItem('lastRoute', currentPath);
  }, [location.pathname]);

  // Sync currentNav with activeNav prop
  useEffect(() => {
    if (currentNav !== activeNav) {
      setCurrentNav(activeNav);
    }

    // Auto-expand sections based on active nav
    setExpandedSections((prev) => {
      let isDifferent = false;
      const next = new Set(prev);

      // Always expand the sectionId if provided
      if (sectionId && !next.has(sectionId)) {
        next.add(sectionId);
        isDifferent = true;
      }

      // Check if activeNav is a nested item and expand its parents
      navigationItems.forEach((item) => {
        if (item.children) {
          // Check if any direct child matches
          const directChildMatch = item.children.some(
            (child) => child.key === activeNav,
          );
          if (directChildMatch && !next.has(item.key)) {
            next.add(item.key);
            isDifferent = true;
          }

          // Check if any nested child (3rd level) matches
          item.children.forEach((subItem) => {
            if (subItem.children) {
              const nestedChildMatch = subItem.children.some(
                (tertiaryItem) => tertiaryItem.key === activeNav,
              );
              if (nestedChildMatch) {
                if (!next.has(item.key)) {
                  next.add(item.key);
                  isDifferent = true;
                }
                if (!next.has(subItem.key)) {
                  next.add(subItem.key);
                  isDifferent = true;
                }
              }
            }
          });
        }
      });

      return isDifferent ? next : prev;
    });
  }, [activeNav, currentNav, sectionId]);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const handleNavClick = (item: NavItem) => {
    if (item.disabled) return;

    if (item.children) {
      if (isSidebarCollapsed) {
        setIsSidebarCollapsed(false);
        localStorage.setItem('settings-sidebar-collapsed', 'false');
        setExpandedSections((previous) => new Set(previous).add(item.key));
      } else {
        toggleSection(item.key);
      }
    } else {
      setCurrentNav(item.key);
      onNavChange?.(item.key);
      if (item.to) {
        navigate(item.to);
      }
    }
  };

  const handleSubNavClick = (item: SubNavItem) => {
    if (item.disabled) return;

    setCurrentNav(item.key);
    onNavChange?.(item.key);
    if (item.to) {
      navigate(item.to);
    }
  };

  const handleToggleCollapse = () => {
    const newCollapsed = !isSidebarCollapsed;
    setIsSidebarCollapsed(newCollapsed);
    localStorage.setItem('settings-sidebar-collapsed', String(newCollapsed));
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      // Get the route we saved when entering settings
      const returnRoute = sessionStorage.getItem('settingsReturnRoute');

      // Navigate back to the previous page, or home if no return route
      if (returnRoute) {
        navigate(returnRoute);
        sessionStorage.removeItem('settingsReturnRoute');
      } else {
        navigate('/');
      }
    }
  };

  const openPortal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="fixed inset-0 mt-[60px] shadow bg-[#f6f6f7] z-50 flex border-t-8 border-[#262626]">
      {/* Left Sidebar */}
      <div
        className={classNames(
          'bg-[#1e293b] border-r border-slate-700/50 flex flex-col shadow-lg',
          isSidebarCollapsed ? 'w-16' : 'w-72',
        )}
        style={{
          transition:
            isSidebarCollapsed !== undefined ? 'width 0.2s ease-out' : 'none',
        }}
      >
        {/* Toggle Button and Store Header */}
        <div className="border-b border-slate-700/50">
          {/* Toggle Button - Only show when expanded, hide when collapsed */}
          {!isSidebarCollapsed && (
            <div className="flex items-center justify-end p-2">
              <button
                type="button"
                onClick={handleToggleCollapse}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-300 hover:bg-slate-700/50 transition-colors"
                title="Collapse sidebar"
              >
                <SidebarToggleIcon className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Store Header */}
          <div
            className={classNames(
              'relative',
              isSidebarCollapsed ? 'p-2' : 'p-4',
            )}
          >
            {isSidebarCollapsed ? (
              /* Collapsed Layout: Toggle button at top, logo centered below */
              <div className="flex flex-col items-center gap-3">
                {/* Toggle Button - Top when collapsed */}
                <button
                  type="button"
                  onClick={handleToggleCollapse}
                  className="p-1.5 rounded-md text-slate-400 hover:text-slate-300 hover:bg-slate-700/50 transition-colors"
                  title="Expand sidebar"
                >
                  <SidebarToggleIcon className="w-4 h-4" />
                </button>

                {/* Logo */}
                <div className="bg-pink-500 rounded-lg flex items-center justify-center text-slate-50 font-semibold overflow-hidden w-10 h-10 flex-shrink-0">
                  {storeProperties?.logoUrl && !logoError ? (
                    <img
                      src={storeProperties.logoUrl}
                      alt={storeName || 'Store logo'}
                      className="w-full h-full object-cover"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    storeName?.charAt(0).toUpperCase()
                  )}
                </div>
              </div>
            ) : (
              /* Expanded Layout: Horizontal layout */
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-lg flex items-center justify-center text-[#1e293b] font-semibold overflow-hidden flex-shrink-0 w-10 h-10">
                  {storeProperties?.logoUrl && !logoError ? (
                    <img
                      src={storeProperties.logoUrl}
                      alt={storeName || 'Store logo'}
                      className="w-full h-full object-cover"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    storeName?.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-semibold text-sm text-slate-50 truncate">
                    {storeName}
                  </h2>
                  <p className="text-xs text-slate-50 truncate">
                    {storeProperties?.store}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navigation Items */}
        <div className="flex-1 overflow-y-auto">
          <nav className="p-2">
            {!isSidebarCollapsed ? (
              <div className="space-y-0">
                {navigationItems.map((item) => (
                  <div key={item.key}>
                    <button
                      onClick={() => handleNavClick(item)}
                      aria-expanded={
                        item.children
                          ? expandedSections.has(item.key)
                          : undefined
                      }
                      className={classNames(
                        'w-full flex items-center gap-3 px-3 py-2 min-h-[44px] text-[11.9px] leading-4 rounded-lg transition-colors',
                        item.disabled
                          ? 'text-slate-500 cursor-not-allowed opacity-60'
                          : currentNav === item.key && !item.children
                          ? 'bg-slate-700/50 text-slate-50 font-medium'
                          : 'text-slate-50 hover:bg-slate-700/50',
                      )}
                      disabled={item.disabled}
                    >
                      <span
                        className={
                          item.disabled ? 'text-slate-500' : 'text-slate-50'
                        }
                      >
                        {item.icon}
                      </span>
                      <span className="flex-1 text-left">{item.name}</span>
                      {item.badge && (
                        <span className="rounded-full border border-slate-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                          {item.badge}
                        </span>
                      )}
                      {item.children &&
                        !item.disabled &&
                        (expandedSections.has(item.key) ? (
                          <ChevronDownIcon className="w-4 h-4 text-slate-400" />
                        ) : (
                          <ChevronRightIcon className="w-4 h-4 text-slate-400" />
                        ))}
                    </button>

                    {/* Sub-navigation items */}
                    {item.children && expandedSections.has(item.key) && (
                      <div
                        className="ml-9 mt-0.5 space-y-0"
                        role="group"
                        aria-label={`${item.name} settings`}
                      >
                        {item.children.map((subItem) => (
                          <div key={subItem.key}>
                            <button
                              aria-current={
                                currentNav === subItem.key ? 'page' : undefined
                              }
                              onClick={() => {
                                if (subItem.disabled) return;
                                if (subItem.children) {
                                  toggleSection(subItem.key);
                                } else {
                                  handleSubNavClick(subItem);
                                }
                              }}
                              className={classNames(
                                'w-full flex items-center justify-between px-3 py-2 min-h-[36px] text-[11.9px] leading-4 rounded-lg transition-colors',
                                subItem.disabled
                                  ? 'text-slate-500 cursor-not-allowed opacity-60'
                                  : currentNav === subItem.key
                                  ? 'bg-slate-700/50 text-slate-50 font-medium'
                                  : 'text-slate-50 hover:bg-slate-700/50',
                              )}
                              disabled={subItem.disabled}
                            >
                              <span className="text-left">{subItem.name}</span>
                              {subItem.badge && (
                                <span className="rounded-full border border-slate-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                                  {subItem.badge}
                                </span>
                              )}
                              {subItem.children &&
                                !subItem.disabled &&
                                (expandedSections.has(subItem.key) ? (
                                  <ChevronDownIcon className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <ChevronRightIcon className="w-4 h-4 text-slate-400" />
                                ))}
                            </button>

                            {/* 3rd Level Items */}
                            {subItem.children &&
                              expandedSections.has(subItem.key) && (
                                <div className="ml-4 mt-0.5 space-y-0 border-l border-slate-700">
                                  {subItem.children.map((tertiaryItem) => (
                                    <button
                                      key={tertiaryItem.key}
                                      onClick={() =>
                                        handleSubNavClick(tertiaryItem)
                                      }
                                      className={classNames(
                                        'w-full text-left pl-4 pr-3 py-2 min-h-[36px] text-[11.9px] leading-4 rounded-r-lg transition-colors',
                                        tertiaryItem.disabled
                                          ? 'text-slate-600 cursor-not-allowed opacity-60'
                                          : currentNav === tertiaryItem.key
                                          ? 'text-pink-400 font-medium'
                                          : 'text-slate-400 hover:text-slate-200',
                                      )}
                                      disabled={tertiaryItem.disabled}
                                    >
                                      {tertiaryItem.name}
                                    </button>
                                  ))}
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-0">
                {navigationItems.map((item) => (
                  <button
                    key={item.key}
                    onClick={() => handleNavClick(item)}
                    aria-expanded={item.children ? false : undefined}
                    className={classNames(
                      'w-full flex items-center justify-center px-2 py-2 min-h-[44px] text-[11.9px] leading-4 rounded-lg transition-colors',
                      item.disabled
                        ? 'text-slate-500 cursor-not-allowed opacity-60'
                        : currentNav === item.key && !item.children
                        ? 'bg-slate-700/50 text-slate-50 font-medium'
                        : 'text-slate-50 hover:bg-slate-700/50',
                    )}
                    title={
                      item.disabled
                        ? `${item.name} - ${item.badge || 'Coming soon'}`
                        : item.name
                    }
                    disabled={item.disabled}
                  >
                    {item.icon}
                  </button>
                ))}
              </div>
            )}
          </nav>
        </div>

        <div className="flex-shrink-0 flex flex-col border-t border-slate-700/50 px-2 pt-2">
          {portalActions.map(({ icon: Icon, ...item }) =>
            !isSidebarCollapsed ? (
              <button
                key={item.key}
                type="button"
                onClick={() => openPortal(item.url)}
                className="w-full mb-2 rounded-md bg-slate-950 text-white flex gap-2 items-center px-3 py-2.5 min-h-[44px] text-[11.9px] leading-4 font-semibold justify-start cursor-pointer shadow-sm hover:bg-blue-700 active:scale-[0.99] transition"
              >
                <Icon className="w-6 h-6 flex-shrink-0" />
                <span className="truncate">{item.name}</span>
              </button>
            ) : (
              <button
                key={item.key}
                type="button"
                onClick={() => openPortal(item.url)}
                className="w-full mb-2 rounded-md bg-slate-950 text-white flex gap-2 items-center justify-center px-2 py-2.5 min-h-[44px] text-[11.9px] leading-4 font-semibold cursor-pointer shadow-sm hover:bg-blue-700 active:scale-[0.99] transition"
                title={item.name}
              >
                <Icon className="w-6 h-6 flex-shrink-0" />
              </button>
            ),
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="sticky top-0 z-10 border-b border-[#e1e3e5] bg-[#f6f6f7]/95 backdrop-blur-sm">
          <div className="px-4 sm:px-6 md:px-8 py-4">
            <button
              onClick={handleClose}
              className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-[#334155] hover:bg-slate-200/70 transition-colors"
              aria-label={exitButtonLabel}
              title={exitButtonLabel}
            >
              <ArrowLeftIcon className="w-4 h-4" />
              <span>{exitButtonLabel}</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto">
          {activeNav?.startsWith('resolution-portal/returns') && (
            <ReturnsScopeWarning
              hasReturnsScope={storeProperties?.hasReturnsScope}
            />
          )}
          {children}
        </div>
      </div>
    </div>
  );
};

export default SettingsViewWrapper;
